import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FormikProps } from 'formik'
import { PrimeIcons } from 'primereact/api'
import { Button } from 'primereact/button'
import { Messages } from 'primereact/messages'

import { Header } from '../../../../../components/Header'
import { CustomConfirmDialog } from '../../../../../components/dialogs'
import { ActiveBodyTemplate } from '../../../../../components/tableTemplates'
import { EditSupplierFormDto, SupplierDetail } from '../../data'

type Props = {
  supplier?: SupplierDetail
  isLoading: boolean
  isEdit: boolean
  setIsEdit: React.Dispatch<React.SetStateAction<boolean>>
  isRequestRunning: boolean
  formik: FormikProps<EditSupplierFormDto>
  activateSupplier: () => Promise<void>
  deactivateSupplier: () => Promise<void>
}

export const Title: React.FC<Props> = ({
  supplier,
  isLoading,
  isEdit,
  setIsEdit,
  isRequestRunning,
  formik,
  activateSupplier,
  deactivateSupplier,
}) => {
  const { t } = useTranslation()
  const messagesRef = useRef<Messages>(null)
  const [isVisible, setIsVisible] = useState(false)

  const handleShow = () => setIsVisible(true)
  const handleHide = () => setIsVisible(false)

  async function handleActivate() {
    await activateSupplier()
  }

  async function handleSave() {
    await formik.submitForm()
  }

  const ActivateButton = () => {
    return (
      <Button
        key="btn-activate"
        label={t('supplier.title.activate.label')}
        severity="success"
        className="p-button mr-2 w-full"
        onClick={handleActivate}
        disabled={supplier?.HasChangesPending || isEdit}
        loading={isRequestRunning}
      />
    )
  }

  const DeactivateButton = () => {
    return (
      <Button
        key="btn-activate"
        label={t('supplier.title.deactivate.label')}
        className="p-button mr-2 w-full"
        onClick={handleShow}
        disabled={supplier?.HasChangesPending || isEdit}
        loading={isRequestRunning}
        outlined
      />
    )
  }

  const ActivationButton = () => {
    if (isEdit) return null
    if (supplier?.Status === 'active') return <DeactivateButton />
    if (supplier?.Status === 'inactive') return <ActivateButton />
    return null
  }

  useEffect(() => {
    if (supplier?.HasChangesPending) {
      messagesRef.current?.replace({
        id: 'supplier_haschangespending',
        closable: false,
        sticky: true,
        severity: 'warn',
        icon: PrimeIcons.EXCLAMATION_TRIANGLE,
        summary: t('supplier.title.hasChangesPending.messages.summary'),
        detail: t('supplier.title.hasChangesPending.messages.detail'),
      })
    }
  }, [supplier])

  const titleContent = (
    <span className="ml-3">{ActiveBodyTemplate(formik.values.status)}</span>
  )

  const actions = (
    <>
      <ActivationButton />
      {supplier?.Status === 'active' && (
        <>
          {isEdit ? (
            <>
              <Button
                key="cancelBtn"
                label={t('supplier.title.cancel.label')}
                icon={PrimeIcons.TIMES}
                className="p-button-outlined mr-2 w-full"
                onClick={() => {
                  formik.resetForm()
                  setIsEdit(false)
                }}
                disabled={isRequestRunning}
              />
              <Button
                key="saveBtn"
                label={t('supplier.title.edit.save.label')}
                icon={PrimeIcons.CHECK}
                className="w-full"
                onClick={handleSave}
                disabled={!formik.dirty}
                loading={isRequestRunning}
              />
            </>
          ) : (
            <Button
              key="editBtn"
              label={t('supplier.title.edit.label')}
              icon="pi pi-pencil"
              className="w-full"
              onClick={() => setIsEdit(true)}
              disabled={
                isRequestRunning ||
                supplier?.Status !== 'active' ||
                supplier.HasChangesPending
              }
            />
          )}
        </>
      )}
    </>
  )

  return (
    <>
      <Header
        title={supplier?.Name}
        titleContent={titleContent}
        showBackButton={true}
        backUrl="/suppliers"
        actions={actions}
        isLoading={isLoading}
      />

      <Messages ref={messagesRef} />

      <CustomConfirmDialog
        severity="danger"
        isVisible={isVisible}
        header={t('supplier.title.deactivate.dialog.title', {
          supplier: supplier?.Name,
          id: supplier?.Id,
        })}
        message={t('supplier.title.deactivate.dialog.body')}
        onHide={handleHide}
        onReject={handleHide}
        rejectLabel={t('common.buttons.cancel')}
        onAccept={async () => {
          await deactivateSupplier()
        }}
        acceptLabel={t('supplier.title.deactivate.dialog.deactivate.label')}
      />
    </>
  )
}
